// Cart reservation countdown: runs while the cart has items, restarts on every cart change and
// empties the cart when it reaches zero. The deadline is kept in sessionStorage so it survives navigation.
// Because the cart is emptied, this is an enforced time limit (WCAG 2.2.1): shortly before it ends the user
// is warned, visually and through the assertive live region, and can restart it with "Extend time" as many
// times as needed. Pages without a visible countdown get a floating copy of it for the warning period.
(function () {
  const settings = window.cartCountdownSettings;
  if (!settings || customElements.get('cart-countdown')) return;

  const STORAGE_KEY = 'cart-countdown-deadline';
  const EXPIRED_KEY = 'cart-countdown-expired';
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  // One minute of warning (half the duration for very short limits): well over the 20 seconds WCAG asks for.
  const WARNING_MS = Math.min(60000, settings.duration / 2);
  const MESSAGES = {
    warning: (time) => `Your cart will be emptied in ${time}. Select Extend time to keep your items.`,
    extended: (time) => `Time extended. Your items are kept for another ${time}.`,
    expired: 'Time ran out and your cart has been emptied.',
  };

  let deadline = null;
  let intervalId = null;
  let warned = false;
  let toastReturnFocus = null;

  function readDeadline() {
    try {
      return Number(sessionStorage.getItem(STORAGE_KEY)) || null;
    } catch (e) {
      return null;
    }
  }

  function writeDeadline(value) {
    try {
      if (value) sessionStorage.setItem(STORAGE_KEY, value);
      else sessionStorage.removeItem(STORAGE_KEY);
    } catch (e) {}
  }

  function remaining() {
    return deadline ? Math.max(deadline - Date.now(), 0) : null;
  }

  function announce(message, priority) {
    if (window.a11y && window.a11y.announce) window.a11y.announce(message, priority);
  }

  function spokenTime(ms) {
    const seconds = Math.ceil(ms / 1000);
    if (seconds < 60) return `${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
    const minutes = Math.round(seconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
  }

  function tick() {
    const ms = remaining();
    document.dispatchEvent(new CustomEvent('cart-countdown:tick', { detail: { remaining: ms } }));
    if (ms === 0) {
      expire();
    } else if (ms && ms <= WARNING_MS) {
      if (!warned) warn(ms);
    } else {
      warned = false;
    }
  }

  function warn(ms) {
    warned = true;
    announce(MESSAGES.warning(spokenTime(ms)), 'assertive');
    // Wait for the countdown elements to be upgraded and rendered before checking whether one is on screen.
    setTimeout(() => {
      if (remaining() && !hasVisibleCountdown()) showToast();
    });
  }

  function isOnScreen(element) {
    if (element.hidden) return false;
    if (element.checkVisibility && !element.checkVisibility({ visibilityProperty: true, opacityProperty: true })) {
      return false;
    }
    const rect = element.getBoundingClientRect();
    return (
      rect.width > 0 &&
      rect.height > 0 &&
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth
    );
  }

  function hasVisibleCountdown() {
    return Array.from(document.querySelectorAll('cart-countdown')).some(isOnScreen);
  }

  // Floating copy of the countdown with its "Extend time" button, shown during the warning period on pages
  // where no countdown is visible. Focus only moves to the button when nothing else has focus, so keyboard
  // users who are mid-task are never redirected; everyone else gets the assertive announcement.
  function showToast() {
    if (!drawerTemplate || document.querySelector('.cart-countdown--toast')) return;
    const toast = drawerTemplate.content.querySelector('cart-countdown').cloneNode(true);
    toast.classList.remove('cart-countdown--drawer');
    toast.classList.add('cart-countdown--toast');
    (document.getElementById('MainContent') || document.body).append(toast);

    // Remember where keyboard users came from so "Extend time" can send them back there.
    toast.addEventListener('focusin', (event) => {
      if (event.relatedTarget && !toast.contains(event.relatedTarget)) toastReturnFocus = event.relatedTarget;
    });

    const activeElement = document.activeElement;
    const nothingFocused = !activeElement || activeElement === document.body;
    const extendButton = toast.querySelector('[data-countdown-extend]');
    if (nothingFocused && document.hasFocus() && extendButton && !extendButton.hidden) {
      extendButton.focus({ preventScroll: true });
    }
  }

  // Qikify Slide Cart renders its drawer client-side, so the countdown is cloned into it from a template
  // (see theme.liquid) and re-inserted whenever the app re-renders the drawer body.
  const drawerTemplate = document.getElementById('cart-countdown-template');
  let mountFrame;

  function mountInAppDrawer() {
    if (!drawerTemplate || !remaining()) return;
    const drawerBody = document.querySelector('.qsc2-drawer-body');
    if (!drawerBody || drawerBody.querySelector(':scope > cart-countdown')) return;
    drawerBody.prepend(drawerTemplate.content.cloneNode(true));
  }

  function run() {
    if (!intervalId) intervalId = setInterval(tick, 1000);
    tick();
    mountInAppDrawer();
  }

  function start() {
    deadline = Date.now() + settings.duration;
    writeDeadline(deadline);
    run();
  }

  function stop() {
    deadline = null;
    writeDeadline(null);
    clearInterval(intervalId);
    intervalId = null;
    tick();
  }

  function expire() {
    stop();
    announce(MESSAGES.expired, 'assertive');
    fetch(`${root}cart/clear.js`, { method: 'POST', headers: { Accept: 'application/json' } })
      .then((response) => {
        // The cart page renders its line items server-side, so reload it to show the empty state
        // and repeat the announcement once the new page has loaded.
        if (response.ok && document.querySelector('cart-items')) {
          try {
            sessionStorage.setItem(EXPIRED_KEY, '1');
          } catch (e) {}
          window.location.reload();
        }
      })
      .catch((e) => console.error(e));
  }

  // Fired by cart-icon-sync.js after any successful /cart/add|change|update|clear request.
  function onCartChanged() {
    fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => (cart.item_count > 0 ? start() : stop()))
      .catch((e) => console.error(e));
  }

  function formatTime(ms) {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = String(Math.floor(totalSeconds / 60)).padStart(2, '0');
    const seconds = String(totalSeconds % 60).padStart(2, '0');
    return `${minutes}:${seconds}`;
  }

  try {
    if (sessionStorage.getItem(EXPIRED_KEY)) {
      sessionStorage.removeItem(EXPIRED_KEY);
      setTimeout(() => announce(MESSAGES.expired, 'assertive'), 1000);
    }
  } catch (e) {}

  const storedDeadline = readDeadline();
  if (settings.itemCount === 0) {
    writeDeadline(null);
  } else if (storedDeadline && storedDeadline - Date.now() <= settings.duration) {
    deadline = storedDeadline;
    run();
  } else {
    start();
  }

  document.addEventListener('cart:changed', onCartChanged);
  if (drawerTemplate) {
    new MutationObserver(() => {
      cancelAnimationFrame(mountFrame);
      mountFrame = requestAnimationFrame(mountInAppDrawer);
    }).observe(document.body, { childList: true, subtree: true });
  }
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && deadline) tick();
  });

  customElements.define(
    'cart-countdown',
    class CartCountdown extends HTMLElement {
      constructor() {
        super();
        this.onTick = (event) => this.render(event.detail.remaining);
        this.onExtend = this.onExtend.bind(this);
      }

      connectedCallback() {
        this.timeElement = this.querySelector('[data-countdown-time]');
        this.extendButton = this.querySelector('[data-countdown-extend]');
        if (this.extendButton) this.extendButton.addEventListener('click', this.onExtend);
        document.addEventListener('cart-countdown:tick', this.onTick);
        this.render(remaining());
      }

      disconnectedCallback() {
        document.removeEventListener('cart-countdown:tick', this.onTick);
        if (this.extendButton) this.extendButton.removeEventListener('click', this.onExtend);
      }

      get isToast() {
        return this.classList.contains('cart-countdown--toast');
      }

      render(ms) {
        if (!ms || (this.isToast && ms > WARNING_MS)) {
          this.remove();
          return;
        }
        this.timeElement.textContent = formatTime(ms);
        if (this.extendButton) this.extendButton.hidden = ms > WARNING_MS;
        this.hidden = false;
      }

      onExtend() {
        const hadFocus = this.contains(document.activeElement);
        const returnFocus = toastReturnFocus;
        start();
        announce(MESSAGES.extended(spokenTime(settings.duration)), 'polite');
        if (!hadFocus) return;

        // The button hides again once the time is restarted, so keep focus from falling back to the page start.
        if (this.isToast) {
          toastReturnFocus = null;
          if (returnFocus && returnFocus.isConnected && returnFocus !== document.body) {
            returnFocus.focus({ preventScroll: true });
          }
        } else {
          this.setAttribute('tabindex', '-1');
          this.focus();
        }
      }
    }
  );
})();
